import { useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { JobClient, JobBuilder } from "@/types";
import { Button, Pill } from "@/components/ui";
import { AddClientDrawer } from "./AddClientDrawer";
import { EditClientDrawer } from "./EditClientDrawer";
import { JobBuildersSection } from "./JobBuildersSection";

/** Clients tab (Brief 02). Renders detail.clients[] (server-sorted lead-first);
 *  the single-lead invariant is server-owned, so every action re-fetches
 *  /detail rather than reconciling locally. Zero clients / zero leads are valid
 *  states. */
export function ClientsTab({
  jobId,
  clients,
  builders,
  onChanged,
  onError,
  onNotice,
}: {
  jobId: number;
  clients: JobClient[];
  /** Builder firms on the job — rendered as a section beneath the clients. */
  builders: JobBuilder[];
  onChanged: () => void;
  onError: (message: string) => void;
  onNotice: (kind: "info" | "warn", message: string) => void;
}) {
  // null = closed; "add" = add drawer; a client = edit drawer.
  const [drawer, setDrawer] = useState<JobClient | "add" | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Lead-vacant: clients exist but none is flagged lead (e.g. after a detach
  // that left ≥2 behind). Server-derived from the re-fetched payload.
  const leadVacant = clients.length > 0 && !clients.some((c) => c.is_lead);

  async function setLead(client: JobClient) {
    if (busyId != null) return;
    setBusyId(client.id);
    try {
      await jobsApi.setLeadClient(jobId, client.id);
      onNotice("info", `${client.display} is now the lead client.`);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not set the lead client.");
    } finally {
      setBusyId(null);
    }
  }

  async function detach(client: JobClient) {
    if (busyId != null) return;
    if (!window.confirm(`Remove ${client.display} from this job? The client record is kept.`)) return;
    setBusyId(client.id);
    try {
      const res = await jobsApi.detachClient(jobId, client.id);
      if (res.new_lead_client_id) {
        const promoted = clients.find((c) => c.id === res.new_lead_client_id);
        onNotice("info", promoted ? `${promoted.display} is now the lead client.` : "Lead reassigned to the remaining client.");
      } else if (res.lead_vacant) {
        onNotice("warn", "This job now has no lead client — choose one below.");
      } else {
        onNotice("info", `${client.display} was removed from the job.`);
      }
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not detach the client.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="job-clients">
      <div className="job-clients__head">
        <p className="job-drawer__muted">{clients.length} client{clients.length === 1 ? "" : "s"} linked</p>
        <Button variant="primary" size="sm" onClick={() => setDrawer("add")}>New client</Button>
      </div>

      {leadVacant ? (
        <p className="job-flash job-flash--warn" role="status">
          This job has no lead client — choose one below.
        </p>
      ) : null}

      {clients.length === 0 ? (
        <p className="jobs-list__empty">No clients linked to this job yet. Add one to get started.</p>
      ) : (
        <ul className="job-client-cards">
          {clients.map((client) => {
            const busy = busyId === client.id;
            return (
              <li key={client.id} className={`job-client-card${client.is_lead ? " job-client-card--lead" : ""}`}>
                <div className="job-client-card__main">
                  <div className="job-client-card__title-row">
                    <span className="job-client-card__name">
                      {client.is_company ? <span className="job-client-tag" aria-label="Company">🏢</span> : null}
                      {client.display}
                    </span>
                    {client.is_lead ? <Pill tone="accent">Lead</Pill> : null}
                  </div>
                  {client.email || client.phone ? (
                    <p className="job-client-card__contact">
                      {[client.email, client.phone].filter(Boolean).join(" · ")}
                    </p>
                  ) : null}
                  {clientAddress(client).length > 0 ? (
                    <address className="job-client-card__address">
                      {clientAddress(client).map((line, i) => (
                        <span key={i}>{line}</span>
                      ))}
                    </address>
                  ) : null}
                  {client.notes ? <p className="job-client-card__notes">{client.notes}</p> : null}
                </div>
                <div className="job-client-card__actions">
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => setDrawer(client)}>Edit</Button>
                  {!client.is_lead ? (
                    <Button variant="secondary" size="sm" loading={busy} onClick={() => setLead(client)}>Set as lead</Button>
                  ) : null}
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => detach(client)}>Detach</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <JobBuildersSection
        jobId={jobId}
        builders={builders}
        onChanged={onChanged}
        onError={onError}
        onNotice={onNotice}
      />

      {drawer === "add" ? (
        <AddClientDrawer
          jobId={jobId}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            onNotice("info", "Client linked to the job.");
            onChanged();
          }}
        />
      ) : null}

      {drawer && drawer !== "add" ? (
        <EditClientDrawer
          client={drawer}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            onNotice("info", "Client updated.");
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/** Address lines from the split fields, falling back to the composed `address`
 *  string. Empty array → no address block. */
function clientAddress(client: JobClient): string[] {
  const lines = [
    client.address_line_1,
    client.address_line_2,
    client.address_line_3,
    [client.city, client.county].filter(Boolean).join(", "),
    client.postcode,
  ]
    .map((l) => (l ?? "").trim())
    .filter(Boolean);
  if (lines.length > 0) return lines;
  return client.address && client.address.trim() ? [client.address.trim()] : [];
}
